import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Newspaper, CheckCircle2, X, Globe, Mail, Briefcase, Users as UsersIcon, ListChecks, Wallet, BadgeCheck, Settings2, Loader2, Percent, Gauge, FileText } from "lucide-react";
import { toast } from "sonner";
import { IssueContractDialog } from "@/components/admin/IssueContractDialog";
import { RowBin, ClearArea } from "@/components/admin/Purge";

const STATUS_TINT = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminPublishers() {
  const [apps, setApps] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [active, setActive] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [tab, setTab] = useState("applications");
  const [revealedKey, setRevealedKey] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);  // commercial settings dialog
  const [contractFor, setContractFor] = useState(null);  // iter90n — manual contract shortcut

  const load = async () => {
    try {
      const [a, p, pa] = await Promise.all([
        api.get("/admin/publishers/applications"),
        api.get("/admin/publishers"),
        api.get("/admin/publishers/payouts"),
      ]);
      setApps(a.data); setPublishers(p.data); setPayouts(pa.data);
    } catch (e) {
      toast.error("Failed to load publisher data");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], approved: [], rejected: [] };
    apps.forEach(a => { (g[a.status] || g.pending).push(a); });
    return g;
  }, [apps]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/publishers/applications/${id}/approve`);
      if (data.api_key) {
        setRevealedKey({ key: data.api_key, temp_password: data.temp_password, email: active?.email, email_sent: data.email_sent });
      } else {
        toast.success("Approved");
      }
      setActive(null); setDecisionNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/publishers/applications/${id}/reject`, { reason: decisionNote || "Application did not meet criteria." });
      toast.success("Rejected");
      setActive(null); setDecisionNote("");
      await load();
    } catch (e) {
      toast.error("Reject failed");
    }
  };

  const markPaid = async (pid) => {
    try {
      await api.post(`/admin/publishers/payouts/${pid}/mark-paid`);
      toast.success("Marked paid");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const releaseViaStripe = async (pid) => {
    if (!window.confirm("Release this payout instantly via Stripe Connect? Funds will be transferred to the publisher's Stripe account.")) return;
    try {
      const { data } = await api.post(`/admin/payouts/publisher/${pid}/release`, {});
      toast.success(`Stripe transfer created · ${data.transfer_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    }
  };

  const quickAction = async (p, what) => {
    const payload = what === "pause" ? { active: false }
      : what === "resume" ? { active: true }
      : what === "rev-50" ? { revenue_share_pct: 0.5 }
      : { revenue_share_pct: 0.9 };
    try {
      await api.patch(`/admin/publishers/${p.id}`, payload);
      toast.success(what === "pause" ? `${p.name} paused`
        : what === "resume" ? `${p.name} resumed`
        : `${p.name} now on ${Math.round(payload.revenue_share_pct * 100)}% revenue share`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-publishers-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Publishers</h1>
          <p className="text-sm text-slate-600 mt-1">Lead producers pushing leads into Connexis via API. Review applications, manage payouts.</p>
        </div>
        <div className="flex items-center gap-3">
          <TabBtn active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-applications">Applications ({grouped.pending.length})</TabBtn>
          <TabBtn active={tab === "publishers"} onClick={() => setTab("publishers")} testid="tab-publishers">Active ({publishers.length})</TabBtn>
          <TabBtn active={tab === "anomalies"} onClick={() => setTab("anomalies")} testid="tab-anomalies">Quality anomalies</TabBtn>
          <TabBtn active={tab === "payouts"} onClick={() => setTab("payouts")} testid="tab-payouts">Payouts ({payouts.filter(p => p.status === "requested").length})</TabBtn>
          <div className="ml-auto"><ClearArea area="publishers" onDone={load} label="Clear applications" /></div>
        </div>
      </header>

      {tab === "applications" && (
        <div className="grid lg:grid-cols-3 gap-4">
          {["pending", "approved", "rejected"].map((status) => (
            <div key={status} className="rounded-xl border border-slate-200 bg-white shadow-soft" data-testid={`col-${status}`}>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{status === "pending" ? "Pending review" : status}</div>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TINT[status]}`}>{grouped[status].length}</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {grouped[status].length === 0 ? <div className="px-4 py-10 text-center text-slate-400 text-[12.5px] italic">Empty</div> :
                  grouped[status].map(a => (
                    <div key={a.id} className="group flex items-start gap-1 pr-2 hover:bg-slate-50">
                      <button onClick={() => setActive(a)} className="min-w-0 flex-1 text-left px-4 py-3 transition-colors" data-testid={`app-card-${a.id.slice(0,8)}`}>
                        <div className="font-medium text-slate-900 text-sm">{a.contact_name} · {a.company}</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">{a.email}</div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(a.sectors || []).slice(0, 3).map(s => (
                            <span key={s} className="text-[10px] rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">{s.replace(/-/g, " ")}</span>
                          ))}
                        </div>
                        <div className="mt-2 text-[11px] text-slate-400 font-mono">{new Date(a.created_at).toLocaleDateString("en-GB")} · ~{a.monthly_volume_estimate?.toLocaleString() || "0"} leads/mo</div>
                      </button>
                      <div className="pt-3 opacity-0 transition-opacity group-hover:opacity-100">
                        <RowBin area="publishers" id={a.id} name={`${a.company || a.contact_name}`} onDone={load} />
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "publishers" && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Quality</th>
                <th className="px-4 py-3">Rev share</th>
                <th className="px-4 py-3">Daily cap</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {publishers.length === 0 ? <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400 italic">No publishers yet</td></tr> : publishers.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`publisher-row-${p.id.slice(0, 8)}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.company || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{p.name}<div className="text-[11.5px] text-slate-500">{p.email}</div></td>
                  <td className="px-4 py-3"><QualityBadge q={p.quality} /></td>
                  <td className="px-4 py-3 text-slate-900 font-mono tabular-nums">{((p.revenue_share_pct ?? 0.90) * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{p.daily_lead_cap ? `${p.daily_lead_cap.toLocaleString()} / day` : "Unlimited"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${p.active === false ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{p.active === false ? "Paused" : "Active"}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-[12.5px]">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="px-4 py-3 text-right pr-4">
                    <div className="inline-flex items-center gap-1.5">
                      {p.active === false ? (
                        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => quickAction(p, "resume")} data-testid={`publisher-quick-resume-${p.id.slice(0, 8)}`}>
                          Resume
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => quickAction(p, "pause")} data-testid={`publisher-quick-pause-${p.id.slice(0, 8)}`}>
                          Pause
                        </Button>
                      )}
                      {((p.revenue_share_pct ?? 0.90) > 0.5) ? (
                        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => quickAction(p, "rev-50")} title="Drop rev share to 50% (quality penalty)" data-testid={`publisher-quick-rev50-${p.id.slice(0, 8)}`}>
                          50%
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => quickAction(p, "rev-90")} title="Reset rev share to default 90%" data-testid={`publisher-quick-rev90-${p.id.slice(0, 8)}`}>
                          90%
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px]" onClick={() => setSettingsFor(p)} data-testid={`publisher-settings-${p.id.slice(0, 8)}`}>
                        <Settings2 className="size-3" /> Settings
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px]" onClick={() => setContractFor(p)} data-testid={`publisher-contract-${p.id.slice(0, 8)}`} title="Issue a contract to this publisher">
                        <FileText className="size-3" /> Contract
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "anomalies" && <QualityAnomalies />}

      {tab === "payouts" && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Publisher</th><th className="px-4 py-3">Method</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th><th /></tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400 italic">No payout requests yet</td></tr> : payouts.map(p => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="px-4 py-3 text-slate-900 text-[13px]">{p.publisher_email}</td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{p.method?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-right font-mono">£{Number(p.amount_gbp).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${p.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {p.status === "requested" && (
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => markPaid(p.id)} data-testid={`payout-mark-paid-${p.id.slice(0,8)}`}>Mark paid</Button>
                        <Button size="sm" className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => releaseViaStripe(p.id)} data-testid={`payout-release-${p.id.slice(0,8)}`}>Release · Stripe</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="app-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Newspaper className="size-5 text-[#0055FF]" /> {active.contact_name} · {active.company}</DialogTitle>
                <DialogDescription>{active.email}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Field Icon={Briefcase} label="Company">{active.company}</Field>
                  <Field Icon={Globe} label="Country">{active.country || "—"}</Field>
                  <Field Icon={Globe} label="Website">{active.website ? <a className="text-[#0055FF] hover:underline" href={active.website} target="_blank" rel="noopener noreferrer">{active.website}</a> : "—"}</Field>
                  <Field Icon={Mail} label="Phone">{active.phone || "—"}</Field>
                  <Field Icon={ListChecks} label="Volume estimate">~{active.monthly_volume_estimate?.toLocaleString() || "0"} leads/mo</Field>
                  <Field Icon={UsersIcon} label="Lead source">{active.lead_source || "—"}</Field>
                  <Field Icon={BadgeCheck} label="Consent capture">{active.consent_capture_method || "—"}</Field>
                  <Field Icon={Wallet} label="Status"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TINT[active.status]}`}>{active.status}</span></Field>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Sectors</div>
                  <div className="flex flex-wrap gap-1.5">{(active.sectors || []).map(s => <span key={s} className="text-[11.5px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">{s.replace(/-/g, " ")}</span>)}</div>
                </div>
                {active.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Notes</div>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[13px] text-slate-700 italic">"{active.notes}"</div>
                  </div>
                )}

                {active.status === "pending" && (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Decision note (optional · used on reject)</div>
                      <Textarea rows={2} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Reason for rejection, or onboarding notes…" data-testid="app-decision-note" />
                    </div>
                    <DialogFooter className="gap-2">
                      <Button variant="outline" onClick={() => reject(active.id)} className="rounded-full" data-testid="app-reject"><X className="size-3.5" /> Reject</Button>
                      <Button onClick={() => approve(active.id)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="app-approve"><CheckCircle2 className="size-3.5" /> Approve · create account & email API key</Button>
                    </DialogFooter>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revealed API key dialog (after approve) */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => !o && setRevealedKey(null)}>
        <DialogContent className="max-w-md" data-testid="revealed-key-dialog">
          <DialogHeader>
            <DialogTitle>Publisher approved ✓</DialogTitle>
            <DialogDescription>{revealedKey?.email_sent ? `API key emailed to ${revealedKey?.email}.` : `Email delivery failed — copy the key below and share it manually with ${revealedKey?.email}.`}</DialogDescription>
          </DialogHeader>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">API key (shown once)</div>
            <pre className="rounded-lg bg-[#0F172A] text-[#3D7BFF] p-4 text-[12px] font-mono break-all whitespace-pre-wrap" data-testid="revealed-key-value">{revealedKey?.key}</pre>
            {revealedKey?.temp_password && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 mt-3">Temp password (publisher must reset on first login)</div>
                <pre className="rounded-lg bg-[#0F172A] text-[#3D7BFF] p-3 text-[12px] font-mono" data-testid="revealed-temp-password">{revealedKey.temp_password}</pre>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(revealedKey?.key || ""); toast.success("Key copied"); }} className="rounded-full" data-testid="revealed-key-copy">Copy key</Button>
            <Button onClick={() => setRevealedKey(null)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-publisher commercial settings (rev share + daily/monthly caps + active + notes) */}
      <PublisherSettingsDialog publisher={settingsFor} onClose={() => setSettingsFor(null)} onSaved={async () => { await load(); setSettingsFor(null); }} />

      {/* iter90n — Manual contract issuance shortcut */}
      <IssueContractDialog
        open={!!contractFor}
        onOpenChange={(o) => !o && setContractFor(null)}
        onIssued={load}
        initialUser={contractFor}
      />
    </div>
  );
}

function PublisherSettingsDialog({ publisher, onClose, onSaved }) {
  const [revPct, setRevPct] = useState("");
  const [dailyCap, setDailyCap] = useState("");
  const [monthlyCap, setMonthlyCap] = useState("");
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!publisher) return;
    setRevPct(((publisher.revenue_share_pct ?? 0.90) * 100).toFixed(0));
    setDailyCap(publisher.daily_lead_cap ?? "");
    setMonthlyCap(publisher.monthly_lead_cap ?? "");
    setActive(publisher.active !== false);
    setNotes(publisher.notes || "");
  }, [publisher]);

  const save = async () => {
    if (!publisher) return;
    const pctNum = parseFloat(revPct);
    if (Number.isNaN(pctNum) || pctNum < 0 || pctNum > 100) {
      toast.error("Rev share must be between 0 and 100"); return;
    }
    setBusy(true);
    try {
      const payload = {
        revenue_share_pct: Math.round(pctNum) / 100,
        daily_lead_cap: dailyCap === "" ? 0 : Math.max(0, parseInt(dailyCap, 10) || 0),
        monthly_lead_cap: monthlyCap === "" ? 0 : Math.max(0, parseInt(monthlyCap, 10) || 0),
        active,
        notes: notes || undefined,
      };
      await api.patch(`/admin/publishers/${publisher.id}`, payload);
      toast.success("Publisher settings saved · applied to next ingest");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!publisher} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="publisher-settings-dialog">
        {publisher && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Settings2 className="size-5 text-[#0055FF]" /> {publisher.company || publisher.name}</DialogTitle>
              <DialogDescription>Adjust commercial terms. Changes apply on the next ingested lead — no restart needed.</DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Percent className="size-3" /> Revenue share %</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="number" min={0} max={100} step={1}
                    value={revPct}
                    onChange={(e) => setRevPct(e.target.value)}
                    className="w-28 h-10 font-mono tabular-nums"
                    data-testid="publisher-settings-rev-share"
                  />
                  <span className="text-[12.5px] text-slate-500">% to publisher · Connexis keeps {revPct ? (100 - parseFloat(revPct || "0")).toFixed(0) : "10"}%</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Gauge className="size-3" /> Daily cap</Label>
                  <Input
                    type="number" min={0} max={100000}
                    placeholder="No cap"
                    value={dailyCap}
                    onChange={(e) => setDailyCap(e.target.value)}
                    className="mt-1 h-10 tabular-nums"
                    data-testid="publisher-settings-daily-cap"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">0 / blank = unlimited</div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Gauge className="size-3" /> Monthly cap</Label>
                  <Input
                    type="number" min={0} max={10000000}
                    placeholder="No cap"
                    value={monthlyCap}
                    onChange={(e) => setMonthlyCap(e.target.value)}
                    className="mt-1 h-10 tabular-nums"
                    data-testid="publisher-settings-monthly-cap"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">0 / blank = unlimited</div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <div>
                  <div className="text-[13px] font-medium text-slate-900">Publisher active</div>
                  <div className="text-[11.5px] text-slate-500">Pause to stop accepting any new leads from this publisher.</div>
                </div>
                <Switch checked={active} onCheckedChange={setActive} data-testid="publisher-settings-active" />
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Internal notes</Label>
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Premium source — guaranteed >85% accept rate"
                  className="mt-1"
                  data-testid="publisher-settings-notes"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button onClick={save} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="publisher-settings-save">
                {busy ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="size-4" /> Save changes</>}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`text-[12.5px] rounded-full px-3 py-1.5 transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={testid}>{children}</button>
  );
}

function Field({ Icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Icon className="size-3" /> {label}</div>
      <div className="mt-1 text-slate-900 text-[13px]">{children}</div>
    </div>
  );
}

// iter58: Publisher Quality Score badge
function QualityBadge({ q }) {
  if (!q || q.tier === "new" || q.score == null) {
    return <span className="inline-flex items-center rounded-full bg-slate-50 text-slate-500 border border-slate-200 px-2 py-0.5 text-[11px] font-medium" title={`Insufficient data — ${q?.sample_size ?? 0} leads in 30d`}>New</span>;
  }
  const tone = {
    gold:   "bg-amber-50 text-amber-700 border-amber-200",
    silver: "bg-slate-50 text-slate-700 border-slate-300",
    bronze: "bg-orange-50 text-orange-700 border-orange-200",
    watch:  "bg-rose-50 text-rose-700 border-rose-200",
  }[q.tier] || "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}
      title={`30d · accept ${Math.round((q.accept_rate || 0) * 100)}% · kept ${Math.round((q.kept_rate || 0) * 100)}% · sample ${q.sample_size}`}
      data-testid="publisher-quality-badge"
    >
      <span className="size-1.5 rounded-full bg-current opacity-70" />
      {q.tier.charAt(0).toUpperCase() + q.tier.slice(1)} · {q.score}
    </span>
  );
}


function QualityAnomalies() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [pausing, setPausing] = useState(null);

  const load = () => {
    api.get(`/admin/quality-anomalies?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [] }));
  };
  useEffect(() => { load(); }, [days]);

  const pausePublisher = async (row) => {
    if (!window.confirm(`Pause ${row.company || row.email}? They'll stop receiving routing and their API key will reject new leads. Reversible via the Active publishers tab.`)) return;
    setPausing(row.publisher_id);
    try {
      await api.patch(`/admin/publishers/${row.publisher_id}`, { active: false });
      toast.success(`${row.company || row.email} paused`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pause failed");
    } finally {
      setPausing(null);
    }
  };

  const sevTint = {
    high:   "bg-rose-50 text-rose-700 border-rose-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low:    "bg-slate-100 text-slate-600 border-slate-200",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft" data-testid="anomalies-panel">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Quality anomalies</div>
          <div className="text-[12px] text-slate-500">Publishers whose buyers BOTH mark leads as <span className="text-emerald-700 font-medium">sold</span> AND <span className="text-rose-700 font-medium">return</span> them — a tell-tale of low-intent traffic.</div>
        </div>
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 text-[11px]">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full transition-colors ${days === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`anomalies-period-${d}`}>{d}d</button>
          ))}
        </div>
      </div>
      {!data ? (
        <div className="px-5 py-10 text-center text-slate-400 text-[12.5px]"><Loader2 className="size-4 animate-spin inline mr-1.5" />Computing…</div>
      ) : data.rows.length === 0 ? (
        <div className="px-5 py-12 text-center" data-testid="anomalies-empty">
          <BadgeCheck className="size-6 text-emerald-500 inline mb-2" />
          <div className="text-[13px] text-slate-700">No anomalies in this window — all publishers' buyer dispositions are consistent.</div>
          <div className="text-[11.5px] text-slate-500 mt-1">Min 20 leads required to score. Add postbacks to fill the picture.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="anomalies-table">
            <thead className="bg-slate-50/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Publisher</th>
                <th className="font-medium text-right">Total</th>
                <th className="font-medium text-right">Sold</th>
                <th className="font-medium text-right">Returned</th>
                <th className="font-medium text-right">Junk</th>
                <th className="font-medium text-right">Mismatch</th>
                <th className="font-medium text-right">Junk:Sold</th>
                <th className="font-medium">Severity</th>
                <th className="font-medium pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.publisher_id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`anomaly-row-${i}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-slate-900 text-[13px]">{r.company}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{r.email}</div>
                  </td>
                  <td className="py-3.5 text-right tabular-nums text-slate-700">{r.total}</td>
                  <td className="py-3.5 text-right tabular-nums text-emerald-700 font-medium">{r.sold}</td>
                  <td className="py-3.5 text-right tabular-nums text-rose-700 font-medium">{r.returned}</td>
                  <td className="py-3.5 text-right tabular-nums text-slate-700">{r.junk}</td>
                  <td className="py-3.5 text-right tabular-nums font-mono text-slate-900">{r.mismatch_score.toFixed(1)}</td>
                  <td className="py-3.5 text-right tabular-nums font-mono text-slate-700">{r.junk_to_sold.toFixed(2)}</td>
                  <td className="py-3.5">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${sevTint[r.severity]}`}>{r.severity}</span>
                  </td>
                  <td className="py-3.5 pr-5 text-right">
                    <Button size="sm" variant="outline" disabled={pausing === r.publisher_id}
                            onClick={() => pausePublisher(r)}
                            className="rounded-full h-7 text-[11.5px] text-rose-700 hover:bg-rose-50 border-rose-200"
                            data-testid={`anomaly-pause-${i}`}>
                      {pausing === r.publisher_id ? "Pausing…" : "Pause"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 text-[11px] text-slate-500 border-t border-slate-100">
            <span className="text-slate-700 font-medium">Mismatch score</span> = return_rate × sold_rate × 100. <span className="text-slate-700 font-medium">Junk:Sold</span> = how often the buyer junks vs. closes. Higher values warrant a manual quality review.
          </div>
        </div>
      )}
    </div>
  );
}
